import {
  Box,
  Button,
  Divider,
  Grid,
  TextField,
  Typography
} from "@mui/material";
import dayjs from "dayjs";
import { t } from "i18next";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  DISCREPANCY_MANAGEMENT,
  DISCREPANCY_MANAGEMENT_UPDATE,
  OPERATIONS_MODULE_BASE_PATH
} from "routing/Paths";
import { CF_URLS } from "services/api/endpoints";
import {
  getExchangeStatusById,
  getMessageFromCode,
  getSuggestionType
} from "services/api/query";
import { selectAuth } from "store/slices";
import {
  setError,
  setHasInsufficientRights,
  setType,
  setUpdateSelectedList
} from "store/slices/discrepancy-search";
import {
  clearError,
  setAuthTypeChange,
  setShowAuthSection,
  updateDiscrepancyAuthFormValues,
  updatePreferencesAuthentication
} from "store/slices/discrepancy-update";
import spacing from "styles/spacing";
import {
  authSection,
  container,
  dateFormatWithoutTime,
  dayMonthYearFormat,
  ERROR_TYPES,
  errorMsgs,
  localeDateFormatWithTimeSec,
  N_CHECKER,
  numberConstants,
  VALUE_ASTERISK,
  VALUE_EMPTY_STRING,
  VALUE_Y,
  Y_CHECKER
} from "utils/constants";
import {
  containerDescripancyTypes,
  containerDiscrepancyTitle,
  descrepancyTypeTitle,
  detectedInType,
  discrepancyTypeOrder,
  fileSectionTypes,
  indefiniteDate,
  mediaTypes,
  mediaTypesTitle,
  OMText,
  openMeidaDiscrepancyTypes,
  systemFunctionTitle,
  TRtext
} from "utils/constants/discrepancy-management/DiscrepancySearchConstants";
import {
  dataDogErrorLog,
  findErrorMessage,
  getResponseData,
  handleRefId
} from "utils/helpers";
import { DetailsStyles } from "./DetailsStyles";
import { DiscrepancyDetailsTable } from "./DiscrepancyDetailsTable";

export const getPendingReturnDate = (pendingReturnDate, locale) => {
  if (pendingReturnDate === indefiniteDate) {
    return t("discrepancyManagement.indefinite");
  }
  if (pendingReturnDate) {
    if (dayjs(pendingReturnDate).isValid()) {
      return dayjs(pendingReturnDate)
        .locale(locale)
        .format(dateFormatWithoutTime);
    } else {
      return pendingReturnDate;
    }
  } else {
    return VALUE_EMPTY_STRING;
  }
};
const DiscrepancyDetails = ({
  setIsLoading,
  setThrowErrorMessage,
  setShowPrintModal,
  isLoading,
  isAuthenticated
}) => {
  const { currentBranch, localeByBranch } = useSelector(selectAuth);
  const { openMediaData, containerData, selectedValues, searchFormValues, type: searchType } =
    useSelector(({ discrepancySearch }) => discrepancySearch);
  const { discrepancyAuthFormValues } = useSelector(
    (state) => state.discrepancyUpdate
  );
  const navigate = useNavigate();
  const [data, setData] = useState({});
  const [details, setDetail] = useState([]);
  const [commentList, setCommentList] = useState([]);
  const [index, setIndex] = useState(0);
  const [currentList, setCurrentList] = useState([]);
  const [type, setDiscrepancyType] = useState();
  const [tempLoad, setTempLoad] = useState(false);
  const [runStatus, setRunStatus] = useState(null);
  const [doNotCallMessage, setDoNotCallMessage] = useState("");
  const [currentDiscrepancyId, setCurrentDiscrepancyId] = useState(null);
  const [branchOwnedText, setBranchOwnedText] = useState("");

  let location = useLocation();
  const dispatch = useDispatch();

  const doNotCallCustomer = (fileSectionType, doNotCallPrefValue) => {
    if (
      doNotCallPrefValue === VALUE_Y &&
      (fileSectionType === fileSectionTypes.two ||
        fileSectionType === fileSectionTypes.three ||
        fileSectionType === fileSectionTypes.four)
    ) {
      return true;
    } else {
      return false;
    }
  };

  const getContainerMediaNumber = (row) => {
    let containerNumber =
      row.om_in_transport_flag === Y_CHECKER
        ? `${row.container_number || ""}${OMText}`
        : row.container_number || "";
    if (
      row.customer_id !== row.assigned_to_customer_id &&
      branchOwnedText &&
      !row.container_number?.includes(branchOwnedText)
    ) {
      containerNumber = `${containerNumber} - ${branchOwnedText}`;
    }
    return containerNumber;
  };

  // Sort order mirrors ContainerAuditTable so Next/Prev matches the search list
  const mappedData = useMemo(() => {
    if (!containerData?.length) return [];

    return [...containerData]
      .map((row) => {
        const code =
          row.discrepancy_type_code?.replace(/\s+/g, "").toLowerCase() || "";
        const lookupKey = `${containerDiscrepancyTitle}${code}${row.system_function_id}`;

        return {
          ...row,
          discrepancyType: row.discrepancy_type_code
            ? containerDescripancyTypes[lookupKey]
            : undefined,
          sortCustomerNumber:
            row.assigned_to_customer_number ?? row.customer_number,
          sortMediaNumber: getContainerMediaNumber(row)
        };
      })
      .sort((a, b) => {
        if (a.discrepancyType && b.discrepancyType) {
          const discrepancyOrder =
            discrepancyTypeOrder.indexOf(a.discrepancyType) -
            discrepancyTypeOrder.indexOf(b.discrepancyType);
          if (discrepancyOrder !== 0) return discrepancyOrder;
        }
        if (a.sortCustomerNumber < b.sortCustomerNumber) return -1;
        if (a.sortCustomerNumber > b.sortCustomerNumber) return 1;
        if (a.container_audit_id < b.container_audit_id) return -1;
        if (a.container_audit_id > b.container_audit_id) return 1;
        if (a.sortMediaNumber < b.sortMediaNumber) return -1;
        if (a.sortMediaNumber > b.sortMediaNumber) return 1;
        return 0;
      })
      .map((row, index) => ({ ...row, index }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerData, branchOwnedText]);

  // Sort order mirrors OpenMediaTable so Next/Prev matches the search list
  const omMappedData = useMemo(() => {
    if (!openMediaData?.length) return [];

    return [...openMediaData]
      .map((row) => {
        const code = row.discrepancy_type_code?.replace(/\s+/g, "") || "";
        const lookupKey = `${descrepancyTypeTitle}${code}${row.file_section_type_id}`;

        return {
          ...row,
          discrepancyType: row.discrepancy_type_code
            ? openMeidaDiscrepancyTypes[lookupKey]
            : undefined
        };
      })
      .sort((a, b) => {
        if (a.customer_number < b.customer_number) return -1;
        if (a.customer_number > b.customer_number) return 1;
        if (a.discrepancyType && b.discrepancyType) {
          return a.discrepancyType.localeCompare(b.discrepancyType);
        }
        return 0;
      })
      .map((row, index) => ({ ...row, index }));
  }, [openMediaData]);

  useEffect(() => {
    getBranchOwnedText();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep index/currentList aligned if sort order changes (e.g. branchOwnedText arrives)
  useEffect(() => {
    if (!currentDiscrepancyId) return;
    if (omMappedData?.length) {
      const omIdx = omMappedData.find(
        (item) => item?.open_media_discrepancy_id === currentDiscrepancyId
      )?.index;
      if (omIdx != null) {
        setCurrentList([...omMappedData]);
        setIndex(omIdx);
        return;
      }
    }
    if (mappedData?.length) {
      const containerIdx = mappedData.find(
        (item) => item?.discrepancy_id === currentDiscrepancyId
      )?.index;
      if (containerIdx != null) {
        setCurrentList([...mappedData]);
        setIndex(containerIdx);
      }
    }
  }, [mappedData, omMappedData, currentDiscrepancyId]);

  useEffect(() => {
    let resolvedType = location?.state?.type || searchType || type;
    if (!(omMappedData?.length || mappedData?.length)) {
      return;
    }
    let id =
      location?.state?.value?.openMediaDiscrepancyId ||
      location?.state?.value?.discrepancyId ||
      currentDiscrepancyId;
    if (!id && selectedValues?.length) {
      id =
        selectedValues[0]?.openMediaDiscrepancyId ||
        selectedValues[0]?.discrepancyId;
    }
    if (!resolvedType && selectedValues?.length) {
      resolvedType = selectedValues[0]?.openMediaDiscrepancyId
        ? t("discrepancyManagement.openMedia")
        : t("discrepancyManagement.container");
    }
    if (id) {
      setCurrentDiscrepancyId(id);
      getDiscrepancyDetail(id, resolvedType);
      setDiscrepancyType(resolvedType);
    }
    // Only re-load when the opened row or list size changes — not on every remapped array identity
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    location?.state?.value?.openMediaDiscrepancyId,
    location?.state?.value?.discrepancyId,
    location?.state?.type,
    mappedData?.length,
    omMappedData?.length,
    selectedValues,
    searchType
  ]);

  useEffect(() => {
    if (!isLoading && tempLoad) {
      setIsLoading(tempLoad);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const getBranchOwnedText = async () => {
    const branchOwned = await getMessageFromCode(errorMsgs.errorCode80288);
    const text = branchOwned[0]?.descr;
    setBranchOwnedText(text);
  };

  useEffect(() => {
    if (data?.customer_number && isAuthenticated) {
      if (
        discrepancyAuthFormValues?.customer?.number?.trim() !==
        data?.customer_number?.trim()
      ) {
        handleError();
      }
    }
    //eslint-disable-next-line
  }, [isAuthenticated, data?.customer_number]);

  const handleError = async () => {
    setIsLoading(true);
    const errDesc = await getMessageFromCode(errorMsgs.errorCode64097);
    if (errDesc[0]?.descr) {
      dispatch(setError(errDesc[0]?.descr));
      dispatch(setHasInsufficientRights(true));
      navigate(`${OPERATIONS_MODULE_BASE_PATH}/${DISCREPANCY_MANAGEMENT}`, {
        state: {
          from: location?.pathname,
          detectedIn: searchFormValues.detectedIn
        }
      });
    }

    setIsLoading(false);
  };

  const getDiscrepancyDetail = async (id, type) => {
    try {
      setTempLoad(true);
      setIsLoading(true);
      let url =
        type === t("discrepancyManagement.openMedia")
          ? CF_URLS.discrepancyManagement.getOpenMediaDiscrepanciessbwebandss
          : CF_URLS.discrepancyManagement
              .getOpenMediaLoadCantainerDiscrepancydetails;
      let req = {};
      if (type === t("discrepancyManagement.openMedia")) {
        req = {
          main_district_id: currentBranch?.value,
          om_discrepancy_id: id
        };
      } else {
        req = {
          main_district_id: currentBranch?.value,
          discrepancy_id: id
        };
      }
      let res = await getResponseData(
        JSON.stringify({
          ...req
        }),
        `${url}`,
        4
      );
      if (res?.data[0][0]) {
        let detected_in = "",
          resolution = "",
          commentText = res?.data[0][0]?.auth_bypass_reason,
          discrepancyTypeModification = "";
        if (
          res?.data[0][0]?.discrepancy_type_code &&
          type === t("discrepancyManagement.openMedia")
        ) {
          discrepancyTypeModification =
            openMeidaDiscrepancyTypes[
              descrepancyTypeTitle +
                res?.data[0][0]?.discrepancy_type_code?.replace(/\s+/g, "") +
                res?.data[0][0]?.file_section_type_id
            ];
        } else {
          discrepancyTypeModification =
            containerDescripancyTypes[
              containerDiscrepancyTitle +
                res?.data[0][0]?.discrepancy_type_code
                  ?.replace(/\s+/g, "")
                  .toLowerCase() +
                res?.data[0][0]?.system_function_id
            ];
        }

        if (res?.data[1]?.length) {
          let data = res?.data[1].map((item) => {
            let temp =
              openMeidaDiscrepancyTypes[
                descrepancyTypeTitle +
                  item?.discrepancy_type_code?.replace(/\s+/g, "") +
                  item?.file_section_type_id
              ];
            return {
              ...item,
              discrepancy_type_code: temp
            };
          });
          setDetail(data);
        }
        setCommentList(res?.data[3]);
        if (res?.data[0][0]?.suggestion_type_id) {
          let res1 = await getSuggestionType(
            res?.data[0][0]?.suggestion_type_id
          );
          resolution = res1[0]?.descr;
        }
        let status = await getExchangeStatusById(
          res?.data[0][0]?.run_exchange_status_id
        );
        if (res?.data[0][0]?.auth_bypass_reason?.trim()) {
          commentText =
            res?.data[0][0]?.auth_bypass_employee_name +
            " " +
            dayjs
              .utc(res?.data[0][0]?.auth_bypass_bypass_date)
              .format(dayMonthYearFormat) +
            " " +
            res?.data[0][0]?.auth_bypass_reason;
        }
        setRunStatus(status);
        let detectedIn = res.data[0]?.find((item) => {
          let id =
            location?.state?.value?.containerAuditId ||
            location?.state?.value?.omAuditId;
          if (!id) {
            id =
              selectedValues[0]?.omAuditId ||
              selectedValues[0]?.containerAuditId;
          }
          return item?.audit_id === id;
        });
        if (!detectedIn) {
          detectedIn = res.data[0][0];
        }
        if (type === t("discrepancyManagement.openMedia")) {
          if (detectedIn?.file_section_type_id === numberConstants.one) {
            detected_in = t("discrepancyManagementDetail.picking");
          } else if (detectedIn?.file_section_type_id === numberConstants.two) {
            detected_in = t("discrepancyManagementDetail.distribution");
          } else if (
            detectedIn?.file_section_type_id === numberConstants.four
          ) {
            detected_in = t("discrepancyManagementDetail.inventory");
          }
          detected_in +=
            " " + detectedIn?.detected_datetime
              ? dayjs
                  .utc(detectedIn?.detected_datetime)
                  .locale(localeByBranch)
                  .format(dateFormatWithoutTime)
              : VALUE_EMPTY_STRING;
          if (detectedIn?.file_section_type_id === numberConstants.sixteen) {
            detected_in =
              t("discrepancyManagementDetail.audit") + detectedIn?.audit_id;
          }
          if (detectedIn?.file_section_type_id === numberConstants.fourTeen) {
            detected_in = t("discrepancyManagement.pickingDestruction");
          }
        } else if (
          [
            detectedIn?.file_section_type_id,
            detectedIn?.system_function_id
          ].includes(numberConstants.seventeethree)
        ) {
          detected_in =
            t("discrepancyManagementDetail.inventoryAudit") +
            detectedIn?.audit_id;
        } else {
          detected_in =
            detectedInType[
              systemFunctionTitle + detectedIn?.system_function_id
            ];
        }
        setData({
          ...res?.data[0][0],
          detected_in,
          resolution,
          commentText,
          discrepancyTypeModification
        });
      }
      if (type === t("discrepancyManagement.openMedia")) {
        if (omMappedData?.length) {
          let idx = omMappedData?.find(
            (item) => item?.open_media_discrepancy_id === id
          )?.index;
          setCurrentList([...omMappedData]);
          setIndex(idx ?? 0);
        } else {
          let idx = openMediaData?.findIndex(
            (item) => item?.open_media_discrepancy_id === id
          );
          setCurrentList([...openMediaData]);
          setIndex(idx >= 0 ? idx : 0);
        }
      } else {
        if (mappedData?.length) {
          let idx = mappedData?.find(
            (item) => item?.discrepancy_id === id
          )?.index;
          setCurrentList([...mappedData]);
          setIndex(idx ?? 0);
        } else {
          let idx = containerData?.findIndex(
            (item) => item?.discrepancy_id === id
          );
          setCurrentList([...containerData]);
          setIndex(idx >= 0 ? idx : 0);
        }
      }
      setTempLoad(false);
      setIsLoading(false);
    } catch (err) {
      const { refId, message } = handleRefId(
        findErrorMessage(ERROR_TYPES.ISSUE)
      );
      dataDogErrorLog(message, "", { error: err }, refId);
      setThrowErrorMessage(message);
      setTempLoad(false);
      setIsLoading(false);
    }
  };
  const handleClickNextPrev = (nextIndex) => {
    const list =
      type === t("discrepancyManagement.openMedia") ? omMappedData : mappedData;
    if (!list?.length || nextIndex < 0 || nextIndex >= list.length) {
      return;
    }
    let id = "";
    if (type === t("discrepancyManagement.openMedia")) {
      id =
        list[nextIndex]?.open_media_discrepancy_id ||
        list[nextIndex]?.discrepancy_id;
    } else {
      id = list[nextIndex]?.discrepancy_id;
    }
    if (!id) {
      return;
    }
    setCurrentDiscrepancyId(id);
    getDiscrepancyDetail(id, type, nextIndex);
  };

  const nextDisableStatus = () => {
    if (type === t("discrepancyManagement.openMedia")) {
      return !omMappedData?.length || index >= omMappedData.length - 1;
    }
    return !mappedData?.length || index >= mappedData.length - 1;
  };
  const handlePersonnelAuthChange = () => {
    const updatedValues = {
      ...discrepancyAuthFormValues,
      personnel: VALUE_EMPTY_STRING,
      authNumber: VALUE_EMPTY_STRING,
      challengeQuestion: VALUE_EMPTY_STRING,
      challengeResponse: VALUE_EMPTY_STRING,
      personnelAuthValue: authSection?.personnelAuth
    };
    dispatch(setAuthTypeChange(authSection?.personnelAuth));
    dispatch(updateDiscrepancyAuthFormValues(updatedValues));
    dispatch(updatePreferencesAuthentication(false));
    dispatch(clearError());
    dispatch(setShowAuthSection(false));
  };
  const handleUpdate = () => {
    const {
      resolved_by_name,
      car_customer_display_flag,
      volser_number,
      customer_file_section_id,
      discrepancy_type_context,
      detected_in_context,
      detected_in_datetime,
      customer_number,
      om_audit_id,
      customer_id,
      customer_name,
      media_type_id,
      request_id,
      last_mod_date,
      file_section_type_id,
      logical_vault_id,
      detected_datetime,
      open_media_discrepancy_id,
      car_id,
      discrepancy_type_code,
      container_number,
      system_function_id,
      discrepancy_id,
      om_in_transport_flag,
      original_system_function_id,
      original_request_id
    } = currentList[index] || {};
    let obj = {
      resolvedByName: resolved_by_name,
      carCustomerDisplayFlag: car_customer_display_flag,
      carId: car_id,
      customerFileSectionId: customer_file_section_id,
      customerId: customer_id,
      customerName: customer_name,
      customerNumber: customer_number,
      detectedDatetime: detected_datetime,
      detectedInContext: detected_in_context,
      detectedInDateTime: detected_in_datetime,
      detectedValue: data?.detected_in,
      requestId: request_id,
      discrepancyTypeCode: discrepancy_type_code,
      discrepancyType: data?.discrepancyTypeModification,
      doNotCallPrefValue: data?.do_not_call_pref_value,
      mediaTypeId: media_type_id,
      logicalVaultId: logical_vault_id,
      discrepancyTypeContext: discrepancy_type_context,
      lastModDate: last_mod_date,
      omAuditId: om_audit_id,
      originalSystemFunctionId: original_system_function_id,
      detectedInFull: data?.detected_in,
      serviceDate: data?.service_date,
      run: data?.run_name,
      pendingReturnDate: data?.pending_return_date,
      customerMediaDescr: currentList[index]?.customer_media_descr,
      runId: data?.run_id ?? "",
      originalRequestId: original_request_id
    };
    if (type === t("discrepancyManagement.openMedia")) {
      obj["fileSectionTypeId"] = file_section_type_id;
      obj["openMediaDiscrepancyId"] = open_media_discrepancy_id;
      obj["volserNumber"] = volser_number;
      obj["omAuditId"] =
        currentList[index]?.om_audit_id ||
        location?.state?.value?.omAuditId ||
        selectedValues[0]?.omAuditId;
      obj["isTransprot"] = om_in_transport_flag;
    } else {
      obj["systemFunctionId"] = system_function_id;
      obj["containerNumber"] = container_number;
      obj["discrepancyId"] = discrepancy_id;
      obj["containerAuditId"] =
        currentList[index]?.container_audit_id ||
        location?.state?.value?.containerAuditId ||
        selectedValues[0]?.containerAuditId;
      obj["hasMedia"] = om_in_transport_flag;
      obj["isTransprot"] = container_number?.includes(TRtext)
        ? Y_CHECKER
        : N_CHECKER;
    }
    handlePersonnelAuthChange();
    dispatch(setUpdateSelectedList([{ ...obj }]));
    dispatch(setType(type));
    navigate(
      `${OPERATIONS_MODULE_BASE_PATH}/${DISCREPANCY_MANAGEMENT_UPDATE}`,
      {
        state: {
          from: location?.pathname,
          detectedIn: searchFormValues.detectedIn
        }
      }
    );
  };
  const handlePrintData = () => {
    let id =
      currentList[index]?.open_media_discrepancy_id ||
      currentList[index]?.discrepancy_id;
    let obj = {
      district_id: currentList[index]?.district_id,
      detected_in: data?.detected_in?.replace(/#/g, "<hash>"),
      om_discrepancy_id: id,
      media_type: data.media_type_id
        ? mediaTypes[mediaTypesTitle + data.media_type_id] ?? data.media_type_id
        : "",
      show_additional_system_comments: numberConstants.one,
      show_bypass_reason: numberConstants.one,
      resolution: data?.resolution,
      location: data?.location,
      run: data?.run_name,
      run_status: runStatus ? runStatus[0]?.type : "",
      pending_return_date: data?.pending_return_date,
      service_date: data?.service_date,
      cstomer_media_identifier: currentList[index]?.customer_media_descr
    };
    setShowPrintModal(obj);
  };

  const getMessage = async () => {
    const message = await getMessageFromCode(errorMsgs.errorCode80280);
    setDoNotCallMessage(message[0]?.descr);
  };

  useEffect(() => {
    if (data) {
      const doNotCall = doNotCallCustomer(
        data?.file_section_type_id,
        data?.do_not_call_pref_value
      );
      if (doNotCall) {
        getMessage();
      } else {
        setDoNotCallMessage("");
      }
    }
  }, [data]);

  let customer = useMemo(() => {
    const resolvedType =
      type || location?.state?.type || searchType || "";
    const isContainer =
      resolvedType === container ||
      resolvedType === t("discrepancyManagement.container");

    // Search tables show assigned_to_customer_number for container rows
    if (isContainer && currentDiscrepancyId) {
      const matchedContainer =
        currentList[index] ||
        containerData.find((c) => c.discrepancy_id === currentDiscrepancyId);

      if (matchedContainer?.assigned_to_customer_number) {
        return matchedContainer.assigned_to_customer_number.trim();
      }
    }

    return (
      data?.customer_number?.trim() ||
      selectedValues[0]?.customerNumber?.trim() ||
      location?.state?.value?.customerNumber?.trim() ||
      VALUE_EMPTY_STRING
    );
  }, [
    type,
    location?.state?.type,
    location?.state?.value?.customerNumber,
    searchType,
    data?.customer_number,
    containerData,
    currentDiscrepancyId,
    currentList,
    index,
    selectedValues
  ]);

  const medisNumber = useMemo(() => {
    const resolvedType = type || location?.state?.type || searchType || "";
    const isOpenMedia =
      resolvedType === t("discrepancyManagement.openMedia") ||
      (!resolvedType &&
        (!!selectedValues[0]?.openMediaDiscrepancyId ||
          !!location?.state?.value?.openMediaDiscrepancyId));

    if (isOpenMedia) {
      return (
        data?.volser_number?.trim() ||
        selectedValues[0]?.volserNumber?.trim() ||
        location?.state?.value?.mediaNumber?.trim() ||
        VALUE_EMPTY_STRING
      );
    }

    const matchedContainer =
      currentList[index] ||
      (currentDiscrepancyId
        ? containerData?.find((c) => c.discrepancy_id === currentDiscrepancyId)
        : null);
    const sourceObj = matchedContainer || data;

    let containerNumber =
      sourceObj?.container_number?.trim() ||
      selectedValues[0]?.containerNumber?.trim() ||
      location?.state?.value?.mediaNumber?.trim() ||
      data?.container_number?.trim();

    if (!containerNumber) return containerNumber || VALUE_EMPTY_STRING;

    // Avoid double-appending suffixes if value already came from selected/display mediaNumber
    const alreadyHasOmSuffix = containerNumber.includes(OMText);
    const alreadyHasBranchText =
      branchOwnedText && containerNumber.includes(branchOwnedText);

    if (
      !alreadyHasOmSuffix &&
      sourceObj?.om_in_transport_flag === Y_CHECKER
    ) {
      containerNumber += OMText;
    }

    const isDifferentCustomer =
      sourceObj?.customer_id != null &&
      sourceObj?.assigned_to_customer_id != null &&
      sourceObj?.customer_id !== sourceObj?.assigned_to_customer_id;

    if (isDifferentCustomer && !alreadyHasBranchText && branchOwnedText) {
      containerNumber += ` - ${branchOwnedText}`;
    }

    return containerNumber;
  }, [
    type,
    location?.state?.type,
    location?.state?.value?.openMediaDiscrepancyId,
    location?.state?.value?.mediaNumber,
    searchType,
    currentDiscrepancyId,
    data,
    containerData,
    branchOwnedText,
    currentList,
    index,
    selectedValues
  ]);

  return (
    <Grid container spacing={2} sx={DetailsStyles.fontColor}>
      <Grid item md={6} lg={3} xs={12}>
        {t("common.Customer")}
        <Typography style={DetailsStyles.fontColor2}>
          {data &&
            (doNotCallCustomer(
              data?.file_section_type_id,
              data?.do_not_call_pref_value
            )
              ? `${customer} ${VALUE_ASTERISK}`
              : customer)}
        </Typography>
      </Grid>
      <Grid item md={6} lg={3} xs={12}>
        {t("discrepancyManagementDetail.customerMediaIdentifier")}
        <Typography style={DetailsStyles.fontColor2}>
          {currentList[index]?.customer_media_descr}
        </Typography>
      </Grid>
      <Grid item md={6} lg={3} xs={12}>
        {t("discrepancyManagementDetail.mediaNumber")}
        <Typography style={DetailsStyles.fontColor2}>{medisNumber}</Typography>
      </Grid>
      <Grid item md={6} lg={3} xs={12}>
        {t("discrepancyManagementDetail.mediaType")}
        <Typography style={DetailsStyles.fontColor2}>
          {data.media_type_id
            ? mediaTypes[mediaTypesTitle + data.media_type_id]
            : ""}
        </Typography>
      </Grid>
      <Grid item md={6} lg={3} xs={12}>
        {t("discrepancyManagementDetail.carId")}
        <Typography style={DetailsStyles.fontColor2}>{data?.car_id}</Typography>
      </Grid>
      <Grid item md={6} lg={3} xs={12}>
        {t("discrepancyManagementDetail.discrepancyType")}
        <Typography style={DetailsStyles.fontColor2}>
          {data?.discrepancyTypeModification}
        </Typography>
      </Grid>
      <Grid item md={6} lg={3} xs={12}>
        {t("discrepancyManagementDetail.detectedDate")}
        <Typography style={DetailsStyles.fontColor2}>
          {data?.detected_datetime
            ? dayjs
                .utc(data?.detected_datetime)
                .locale(localeByBranch)
                .format(localeDateFormatWithTimeSec)
            : VALUE_EMPTY_STRING}
        </Typography>
      </Grid>
      <Grid item md={6} lg={3} xs={12}>
        {t("discrepancyManagementDetail.detectedIn")}
        <Typography style={DetailsStyles.fontColor2}>
          {data?.detected_in}{" "}
        </Typography>
      </Grid>
      {type !== t("discrepancyManagement.openMedia") ? (
        <>
          <Grid item md={6} lg={3} xs={12}>
            {t("discrepancyManagementDetail.serviceDate")}
            <Typography style={DetailsStyles.fontColor2}>
              {data?.service_date
                ? dayjs
                    .utc(data?.service_date)
                    .locale(localeByBranch)
                    .format(dateFormatWithoutTime)
                : VALUE_EMPTY_STRING}
            </Typography>
          </Grid>
          <Grid item md={6} lg={3} xs={12}>
            {t("discrepancyManagementDetail.pendingReturnDate")}
            <Typography style={DetailsStyles.fontColor2}>
              {getPendingReturnDate(data?.pending_return_date, localeByBranch)}
            </Typography>
          </Grid>
          <Grid item md={6} lg={3} xs={12}>
            {t("discrepancyManagementDetail.run")}
            <Typography style={DetailsStyles.fontColor2}>
              {data?.run_name}{" "}
            </Typography>
          </Grid>
          <Grid item md={6} lg={3} xs={12}>
            {t("discrepancyManagementDetail.runStatus")}
            <Typography style={DetailsStyles.fontColor2}>
              {runStatus ? runStatus[0]?.type : ""}
            </Typography>
          </Grid>
        </>
      ) : (
        <>
          <Grid item md={6} lg={3} xs={12}>
            {t("discrepancyManagementDetail.logicalVault")}
            <Typography style={DetailsStyles.fontColor2}>
              {data?.logical_vault_code}{" "}
            </Typography>
          </Grid>
          <Grid item md={6} lg={3} xs={12}>
            {t("discrepancyManagementDetail.requestId")}
            <Typography style={DetailsStyles.fontColor2}>
              {data?.request_id}{" "}
            </Typography>
          </Grid>
        </>
      )}

      <Grid item md={6} lg={3} xs={12}>
        {t("discrepancyManagementDetail.displayToCustomerDate")}
        <Typography style={DetailsStyles.fontColor2}>
          {data?.display_to_customer_datetime
            ? dayjs
                .utc(data?.display_to_customer_datetime)
                .locale(localeByBranch)
                .format(localeDateFormatWithTimeSec)
            : VALUE_EMPTY_STRING}
        </Typography>
      </Grid>

      <Grid item md={6} lg={3} xs={12}>
        {""}
        <Typography>{""}</Typography>
      </Grid>
      {doNotCallMessage && (
        <Grid item md={6} lg={3} xs={12}>
          <Typography>{doNotCallMessage}</Typography>
        </Grid>
      )}

      <Grid item md={12} lg={12} xs={12}>
        <Divider />
      </Grid>
      <Grid item md={6} lg={3} xs={12}>
        {t("discrepancyManagementDetail.resolvedDate")}
        {data.resolved_datetime && (
          <Typography style={DetailsStyles.fontColor2}>
            {data.resolved_datetime
              ? dayjs
                  .utc(data.resolved_datetime)
                  .locale(localeByBranch)
                  .format(localeDateFormatWithTimeSec)
              : VALUE_EMPTY_STRING}
          </Typography>
        )}
      </Grid>
      <Grid item md={6} lg={3} xs={12}>
        {t("discrepancyManagementDetail.resolvedBy")}
        <Typography style={DetailsStyles.fontColor2}>
          {data?.resolved_by_name}
        </Typography>
      </Grid>
      <Grid item md={6} lg={3} xs={12}>
        {t("discrepancyManagementDetail.authorizedBy")}
        <Typography style={DetailsStyles.fontColor2}>
          {data?.authorized_by_name}{" "}
        </Typography>
      </Grid>
      <Grid item md={6} lg={3} xs={12}>
        {t("discrepancyManagementDetail.resolution")}
        <Typography style={DetailsStyles.fontColor2}>
          {data?.resolution}{" "}
        </Typography>
      </Grid>
      {type === t("discrepancyManagement.openMedia") && (
        <>
          <Grid item md={6} lg={3} xs={12}>
            {t("discrepancyManagementDetail.lastKnownLocation")}
            <Typography style={DetailsStyles.fontColor2}>
              {data?.last_known_location}
            </Typography>
          </Grid>
          <Grid item md={6} lg={3} xs={12}>
            {t("discrepancyManagementDetail.location")}
            <Typography style={DetailsStyles.fontColor2}>
              {data?.location}
            </Typography>
          </Grid>
          <Grid item md={12} lg={12} xs={12}>
            <Divider />
          </Grid>
          <Grid item md={12} lg={12} xs={12}>
            <DiscrepancyDetailsTable
              details={details}
              localeByBranch={localeByBranch}
            />
          </Grid>
        </>
      )}
      <Grid item md={12} lg={12} xs={12}>
        <Divider />
      </Grid>
      <Grid item md={12} lg={12} xs={12}>
        <Typography style={DetailsStyles.subHeading}>
          {t("discrepancyManagementDetail.additionalSystemInformation")}
        </Typography>
        <Grid item style={DetailsStyles.sectionStyle}>
          {commentList?.length ? (
            commentList?.map((item) => {
              return (
                <Grid item key={item.comment}>
                  {item.comment}{" "}
                </Grid>
              );
            })
          ) : (
            <>{t("common.noRecordsFound")} </>
          )}
        </Grid>
      </Grid>
      <Grid item md={12} lg={12} xs={12}>
        <Box>
          <TextField
            name="byPassReason"
            id="byPassReason"
            label={t("discrepancyManagementDetail.byPassReason")}
            value={data?.commentText}
            fullWidth
            multiline
            rows={4}
            inputProps={{ maxLength: 225 }}
            disabled={true}
            variant="outlined"
            InputLabelProps={{ shrink: !!data?.commentText }}
          />
        </Box>
      </Grid>
      <Grid item md={12} lg={12} xs={12}>
        <Divider />
      </Grid>
      <Grid item md={12} lg={12} xs={12}>
        <Grid
          item
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="start"
          gap={spacing.verticalMargin20}
        >
          <Button
            variant="outlined"
            onClick={() => {
              handlePersonnelAuthChange();
              navigate(
                `${OPERATIONS_MODULE_BASE_PATH}/${DISCREPANCY_MANAGEMENT}`,
                {
                  state: {
                    from: location?.pathname,
                    detectedIn: searchFormValues.detectedIn
                  }
                }
              );
            }}
            disabled={isAuthenticated}
          >
            {t("common.back")}
          </Button>
          <Button
            variant="outlined"
            disabled={index === 0}
            onClick={() => {
              handleClickNextPrev(index - 1);
            }}
          >
            {t("discrepancyManagementDetail.previous")}
          </Button>
          <Button
            variant="outlined"
            disabled={nextDisableStatus()}
            onClick={() => {
              handleClickNextPrev(index + 1);
            }}
          >
            {t("discrepancyManagementDetail.next")}
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              handlePrintData();
            }}
          >
            {t("discrepancyManagementDetail.print")}
          </Button>
          <Button
            variant="contained"
            size="medium"
            onClick={() => {
              handleUpdate();
            }}
            disabled={
              [numberConstants.two, numberConstants.one].includes(
                data?.audit_status_id
              ) || data?.resolved_datetime
            }
          >
            {t("discrepancyManagementDetail.updateDiscrepancy")}
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default DiscrepancyDetails;
